import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiChevronLeft, FiChevronRight, FiEdit2, FiFilter, FiPlus, FiRefreshCw, FiSearch, FiTrash2, FiX } from 'react-icons/fi';
import { CustomersService, JewelryService, LocationsService, SalesService } from '../services/ZashaniService';
import { useAuth } from '../context/AuthContext';
import { ButtonSpinner, PanelLoader, TableEmptyState } from '../components/LoadingStates';
import { codChargeTypeLabels, labelFor, shippingModeLabels, shippingProviderLabels, statusLabels } from '../utils/labels';

const currency = new Intl.NumberFormat('es-GT', { style: 'currency', currency: 'GTQ' });
const emptySale = {
    customerId: '',
    customer: { fullName: '', phone: '', address: '', location: { department: '', municipality: '', zone: '' } },
    shipDate: '',
    items: [{ code: '', salePrice: '' }],
    deposit: { hasDeposit: false, amount: 0, type: 'partial', bank: 'BI' },
    shipping: { provider: 'mensajero', mode: 'standard', customerCharge: 25, codChargeType: 'pending_balance' },
    notes: '',
};

const initialFilters = {
    mode: 'all',
    day: '',
    month: '',
    from: '',
    to: '',
};

const statusOptions = ['registered', 'pending_payment', 'pending_shipping', 'shipped', 'delivered', 'cancelled'];
const shippingProviders = ['mensajero', 'forza', 'caex'];
const codChargeTypes = ['pending_balance', 'shipping_only', 'order_total'];
const depositTypeLabels = { none: 'Sin deposito', partial: 'Parcial', total: 'Total' };
const zoneOptions = Array.from({ length: 25 }, (_, index) => `Zona ${index + 1}`);
const CUSTOMER_PAGE_SIZE = 5;
const SALES_PAGE_SIZE = 25;

const toDateInput = (value) => {
    if (!value) return '';
    return new Date(value).toISOString().slice(0, 10);
};

const formatDateInput = (date) => date.toISOString().slice(0, 10);

const formatDisplayDate = (value) => {
    if (!value) return 'Pendiente';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Pendiente';
    return new Intl.DateTimeFormat('es-GT', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
};

const formatDisplayDateOnly = (value) => {
    if (!value) return 'Pendiente';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Pendiente';
    return new Intl.DateTimeFormat('es-GT', { dateStyle: 'medium', timeZone: 'UTC' }).format(date);
};

const getMonthRange = (monthValue) => {
    if (!monthValue) return {};
    const [year, month] = monthValue.split('-').map(Number);
    const from = new Date(year, month - 1, 1);
    const to = new Date(year, month, 0);
    return { dateFrom: formatDateInput(from), dateTo: formatDateInput(to) };
};

const getFilterRange = (filters) => {
    if (filters.mode === 'day' && filters.day) {
        return { dateFrom: filters.day, dateTo: filters.day };
    }
    if (filters.mode === 'month') {
        return getMonthRange(filters.month);
    }
    if (filters.mode === 'range') {
        return {
            dateFrom: filters.from || undefined,
            dateTo: filters.to || undefined,
        };
    }
    return {};
};

const Sales = () => {
    const { user } = useAuth();
    const [sales, setSales] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [customerSearch, setCustomerSearch] = useState('');
    const [customerPage, setCustomerPage] = useState({ total: 0, page: 1, pages: 1, limit: CUSTOMER_PAGE_SIZE });
    const [customerLoading, setCustomerLoading] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerMode, setCustomerMode] = useState('existing');
    const [jewelry, setJewelry] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [form, setForm] = useState(emptySale);
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [filters, setFilters] = useState(initialFilters);
    const [salesSearch, setSalesSearch] = useState('');
    const [salesPage, setSalesPage] = useState({ total: 0, page: 1, pages: 1, limit: SALES_PAGE_SIZE });
    const [editingSaleId, setEditingSaleId] = useState(null);
    const [saleEdit, setSaleEdit] = useState(null);
    const [saleSaving, setSaleSaving] = useState(false);
    const [selectedSale, setSelectedSale] = useState(null);

    const filterRange = useMemo(() => getFilterRange(filters), [filters]);
    const currentSaleDateLabel = useMemo(
        () => new Intl.DateTimeFormat('es-GT', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date()),
        [],
    );

    const jewelryTotal = useMemo(
        () => form.items.reduce((sum, item) => sum + Number(item.salePrice || 0), 0),
        [form.items],
    );

    const filteredSales = sales;

    const loadCustomers = useCallback(async ({ page = 1, search = customerSearch } = {}) => {
        setCustomerLoading(true);
        try {
            const data = await CustomersService.listPage({
                search: search.trim() || undefined,
                page,
                limit: CUSTOMER_PAGE_SIZE,
                sort: 'name',
            });
            setCustomers(data.items || []);
            setCustomerPage({
                total: data.total || 0,
                page: data.page || page,
                pages: data.pages || 1,
                limit: data.limit || CUSTOMER_PAGE_SIZE,
            });
        } catch (error) {
            setMessage(error.response?.data?.message || 'No fue posible cargar clientes.');
        } finally {
            setCustomerLoading(false);
        }
    }, [customerSearch]);

    const load = useCallback(async ({ rangeOverride = filterRange, page = 1, search = salesSearch } = {}) => {
        setLoading(true);
        try {
            const [saleRows, jewelryRows, locationRows] = await Promise.all([
                SalesService.listPage({
                    ...rangeOverride,
                    search: search.trim() || undefined,
                    page,
                    limit: SALES_PAGE_SIZE,
                }),
                JewelryService.list(),
                LocationsService.departments(),
            ]);
            setSales(saleRows.items || []);
            setSalesPage({
                total: saleRows.total || 0,
                page: saleRows.page || page,
                pages: saleRows.pages || 1,
                limit: saleRows.limit || SALES_PAGE_SIZE,
            });
            setJewelry(jewelryRows);
            setDepartments(locationRows);
        } catch (error) {
            setMessage(error.response?.data?.message || 'No fue posible cargar los datos.');
        } finally {
            setLoading(false);
        }
    }, [filterRange, salesSearch]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            load({ page: 1 });
        }, 280);
        return () => clearTimeout(timeout);
    }, [load]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            loadCustomers({ page: 1 });
        }, 280);
        return () => clearTimeout(timeout);
    }, [loadCustomers]);

    const applyFilters = async (event) => {
        event.preventDefault();
        await load();
    };

    const clearFilters = async () => {
        setFilters(initialFilters);
        setSalesSearch('');
        await load({ rangeOverride: {}, page: 1, search: '' });
    };

    const updateItem = (index, patch) => {
        setForm((current) => ({
            ...current,
            items: current.items.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)),
        }));
    };

    const submit = async (event) => {
        event.preventDefault();
        setMessage('');
        if (customerMode === 'existing' && !form.customerId) {
            setMessage('Selecciona un cliente existente o presiona Nuevo para crear uno.');
            return;
        }
        setSaving(true);
        try {
            const payload = {
                ...form,
                customerId: customerMode === 'existing' ? form.customerId : undefined,
                customer: customerMode === 'new' ? form.customer : undefined,
                items: form.items.map((item) => ({ code: item.code, salePrice: Number(item.salePrice) })),
                deposit: {
                    ...form.deposit,
                    amount: Number(form.deposit.amount || 0),
                },
                shipping: {
                    ...form.shipping,
                    customerCharge: Number(form.shipping.customerCharge || 0),
                },
            };
            await SalesService.create(payload);
            setForm(emptySale);
            setSelectedCustomer(null);
            setCustomerMode('existing');
            setCustomerSearch('');
            setMessage('Venta registrada.');
            await Promise.all([load({ page: 1 }), loadCustomers({ page: 1, search: '' })]);
        } catch (error) {
            setMessage(error.response?.data?.message || 'No fue posible registrar la venta.');
        } finally {
            setSaving(false);
        }
    };

    const shippingModeOptions = form.shipping.provider === 'mensajero'
        ? ['standard']
        : form.shipping.provider === 'caex'
            ? ['standard', 'cod', 'collect']
            : ['standard', 'cod'];

    const editShippingModeOptions = saleEdit?.shipping.provider === 'mensajero'
        ? ['standard']
        : saleEdit?.shipping.provider === 'caex'
            ? ['standard', 'cod', 'collect']
            : ['standard', 'cod'];

    const customerMunicipalities = departments.find((department) => department.name === form.customer.location.department)?.municipalities || [];
    const editMunicipalities = departments.find((department) => department.name === saleEdit?.customerSnapshot?.location?.department)?.municipalities || [];

    const selectExistingCustomer = (customer) => {
        setSelectedCustomer(customer);
        setCustomerMode('existing');
        setForm((current) => ({ ...current, customerId: customer._id, customer: emptySale.customer }));
    };

    const startNewCustomer = () => {
        setSelectedCustomer(null);
        setCustomerMode('new');
        setForm((current) => ({ ...current, customerId: '' }));
    };

    const showExistingCustomers = () => {
        setSelectedCustomer(null);
        setCustomerMode('existing');
        setForm((current) => ({ ...current, customerId: '', customer: emptySale.customer }));
    };

    const beginEditSale = (sale) => {
        setEditingSaleId(sale._id);
        setSaleEdit({
            status: sale.status || 'registered',
            shipDate: toDateInput(sale.shipDate),
            customerSnapshot: {
                address: sale.customerSnapshot?.address || '',
                location: {
                    department: sale.customerSnapshot?.location?.department || '',
                    municipality: sale.customerSnapshot?.location?.municipality || '',
                    zone: sale.customerSnapshot?.location?.zone || '',
                },
            },
            deposit: {
                hasDeposit: Boolean(sale.deposit?.hasDeposit),
                amount: sale.deposit?.amount || 0,
                type: sale.deposit?.type || 'partial',
                bank: sale.deposit?.bank || 'BI',
            },
            shipping: {
                provider: sale.shipping?.provider || 'mensajero',
                mode: sale.shipping?.mode || 'standard',
                customerCharge: sale.shipping?.customerCharge || 25,
                codChargeType: sale.shipping?.codChargeType || 'pending_balance',
            },
            notes: sale.notes || '',
        });
    };

    const cancelEditSale = () => {
        setEditingSaleId(null);
        setSaleEdit(null);
    };

    const submitSaleEdit = async (event) => {
        event.preventDefault();
        setSaleSaving(true);
        setMessage('');
        try {
            await SalesService.update(editingSaleId, {
                ...saleEdit,
                deposit: {
                    ...saleEdit.deposit,
                    amount: Number(saleEdit.deposit.amount || 0),
                },
                shipping: {
                    ...saleEdit.shipping,
                    customerCharge: Number(saleEdit.shipping.customerCharge || 0),
                },
            });
            setMessage('Venta actualizada.');
            cancelEditSale();
            await load({ page: salesPage.page });
        } catch (error) {
            setMessage(error.response?.data?.message || 'No fue posible actualizar la venta.');
        } finally {
            setSaleSaving(false);
        }
    };

    const deleteSale = async (sale) => {
        if (!window.confirm(`Eliminar venta de ${sale.customerSnapshot?.fullName || 'cliente'}?`)) return;
        setSaleSaving(true);
        setMessage('');
        try {
            await SalesService.delete(sale._id);
            setMessage('Venta eliminada.');
            await load({ page: salesPage.page });
        } catch (error) {
            setMessage(error.response?.data?.message || 'No fue posible eliminar la venta.');
        } finally {
            setSaleSaving(false);
        }
    };

    const renderSaleBreakdown = (sale) => {
        const isAdmin = user.role === 'admin';
        const totalItems = sale.items?.length || 0;

        return (
            <div className="sale-modal-backdrop" role="presentation" onClick={() => setSelectedSale(null)}>
                <article className="sale-modal sale-breakdown" role="dialog" aria-modal="true" aria-labelledby="sale-breakdown-title" onClick={(event) => event.stopPropagation()}>
                    <button className="modal-close-button" type="button" onClick={() => setSelectedSale(null)} aria-label="Cerrar desglose">
                        <FiX />
                    </button>
                        <div className="breakdown-header">
                            <div>
                                <p className="eyebrow">Desglose de venta</p>
                                <h3 id="sale-breakdown-title">{sale.customerSnapshot?.fullName || 'Cliente sin nombre'}</h3>
                                <span>{totalItems} pieza{totalItems === 1 ? '' : 's'} registrada{totalItems === 1 ? '' : 's'}</span>
                            </div>
                            <div className="breakdown-pills">
                                <span>{labelFor(statusLabels, sale.status)}</span>
                                <span>Envio: {formatDisplayDateOnly(sale.shipDate)}</span>
                            </div>
                        </div>

                        <div className="breakdown-grid">
                            <section className="breakdown-section">
                                <h4>Cliente</h4>
                                <dl className="breakdown-list">
                                    <div><dt>Telefono</dt><dd>{sale.customerSnapshot?.phone || 'Sin telefono'}</dd></div>
                                    <div><dt>Departamento</dt><dd>{sale.customerSnapshot?.location?.department || 'Sin departamento'}</dd></div>
                                    <div><dt>Municipio</dt><dd>{sale.customerSnapshot?.location?.municipality || 'Sin municipio'}</dd></div>
                                    <div><dt>Zona</dt><dd>{sale.customerSnapshot?.location?.zone || 'Sin zona'}</dd></div>
                                    <div><dt>Direccion</dt><dd>{sale.customerSnapshot?.address || 'Sin direccion'}</dd></div>
                                    <div><dt>Registro</dt><dd>{formatDisplayDate(sale.createdAt)}</dd></div>
                                </dl>
                            </section>

                            <section className="breakdown-section">
                                <h4>Deposito</h4>
                                <dl className="breakdown-list">
                                    <div><dt>Estado</dt><dd>{sale.deposit?.hasDeposit ? 'Con deposito' : 'Sin deposito'}</dd></div>
                                    <div><dt>Tipo</dt><dd>{labelFor(depositTypeLabels, sale.deposit?.type)}</dd></div>
                                    <div><dt>Banco</dt><dd>{sale.deposit?.bank || 'No aplica'}</dd></div>
                                    <div><dt>Monto</dt><dd>{currency.format(sale.deposit?.amount || 0)}</dd></div>
                                </dl>
                            </section>

                            <section className="breakdown-section">
                                <h4>Envio</h4>
                                <dl className="breakdown-list">
                                    <div><dt>Proveedor</dt><dd>{labelFor(shippingProviderLabels, sale.shipping?.provider)}</dd></div>
                                    <div><dt>Modalidad</dt><dd>{labelFor(shippingModeLabels, sale.shipping?.mode)}</dd></div>
                                    <div><dt>COD</dt><dd>{labelFor(codChargeTypeLabels, sale.shipping?.codChargeType)}</dd></div>
                                    <div><dt>Cobro cliente</dt><dd>{currency.format(sale.shipping?.customerCharge || 0)}</dd></div>
                                    <div><dt>Monto COD</dt><dd>{currency.format(sale.shipping?.codAmount || sale.totals?.codAmount || 0)}</dd></div>
                                    <div><dt>Comision COD</dt><dd>{currency.format(sale.shipping?.commission || sale.totals?.codCommission || 0)}</dd></div>
                                    {isAdmin && <div><dt>Costo empresa</dt><dd>{currency.format(sale.shipping?.companyCost || sale.totals?.shippingCompanyCost || 0)}</dd></div>}
                                    {isAdmin && <div><dt>COD real recibido</dt><dd>{currency.format(sale.shipping?.realCodReceived || sale.totals?.realCodReceived || 0)}</dd></div>}
                                </dl>
                            </section>
                        </div>

                        <section className="breakdown-section wide">
                            <div className="breakdown-title-row">
                                <h4>Joyas vendidas</h4>
                                <span>{currency.format(sale.totals?.jewelrySalesTotal || 0)}</span>
                            </div>
                            <div className="items-breakdown">
                                {(sale.items || []).map((item, index) => (
                                    <div className="item-breakdown" key={`${sale._id}-${item.code}-${index}`}>
                                        <strong>{item.code}</strong>
                                        <span>Venta {currency.format(item.salePrice || 0)}</span>
                                        {isAdmin && <span>Costo {currency.format(item.costAtSale || 0)}</span>}
                                        {isAdmin && <span>Margen {currency.format((item.salePrice || 0) - (item.costAtSale || 0))}</span>}
                                    </div>
                                ))}
                            </div>
                        </section>

                        <div className="breakdown-total-grid">
                            <div><span>Joyas</span><strong>{currency.format(sale.totals?.jewelrySalesTotal || 0)}</strong></div>
                            <div><span>Envio cliente</span><strong>{currency.format(sale.totals?.shippingCustomerTotal || 0)}</strong></div>
                            <div><span>Deposito</span><strong>{currency.format(sale.totals?.depositTotal || 0)}</strong></div>
                            <div><span>Total venta</span><strong>{currency.format(sale.totals?.saleTotal || 0)}</strong></div>
                            <div><span>Saldo pendiente</span><strong>{currency.format(sale.totals?.pendingBalance || 0)}</strong></div>
                            {isAdmin && <div><span>Costo joyas</span><strong>{currency.format(sale.totals?.jewelryCostTotal || 0)}</strong></div>}
                            {isAdmin && <div><span>Costo envio</span><strong>{currency.format(sale.totals?.shippingCompanyCost || 0)}</strong></div>}
                            {isAdmin && <div><span>Ganancia bruta</span><strong>{currency.format(sale.totals?.grossProfit || 0)}</strong></div>}
                            {isAdmin && <div><span>Ganancia neta</span><strong>{currency.format(sale.totals?.netProfit || 0)}</strong></div>}
                        </div>

                        {sale.notes && (
                            <section className="breakdown-section wide">
                                <h4>Notas</h4>
                                <p className="breakdown-notes">{sale.notes}</p>
                            </section>
                        )}
                </article>
            </div>
        );
    };

    return (
        <section className="sales-layout">
            <form className={`panel sale-form ${saving ? 'is-busy' : ''}`} onSubmit={submit}>
                {saving && <PanelLoader label="Registrando venta..." />}
                <div className="section-heading compact">
                    <div>
                        <p className="eyebrow">Operacion</p>
                        <h2>Nueva venta</h2>
                        <span className="sale-date-pill">Fecha de venta: {currentSaleDateLabel}</span>
                    </div>
                    <strong>{currency.format(jewelryTotal)}</strong>
                </div>
                {message && <div className="notice">{message}</div>}
                <div className="form-grid">
                    <section className="customer-picker">
                        <div className="customer-picker-header">
                            <div>
                                <span>Cliente existente</span>
                                <small>{customerPage.total} cliente{customerPage.total === 1 ? '' : 's'} encontrado{customerPage.total === 1 ? '' : 's'}</small>
                            </div>
                            <button
                                className={customerMode === 'new' ? 'active' : ''}
                                type="button"
                                onClick={customerMode === 'new' ? showExistingCustomers : startNewCustomer}
                            >
                                {customerMode === 'new' ? <FiSearch /> : <FiPlus />}
                                <span>{customerMode === 'new' ? 'Buscar existente' : 'Nuevo'}</span>
                            </button>
                        </div>
                        {customerMode === 'new' ? (
                            <div className="customer-picker-collapsed">
                                <div>
                                    <strong>Cliente nuevo</strong>
                                    <span>La lista de clientes existentes queda retraida mientras registras los datos.</span>
                                </div>
                                <button className="ghost-button muted" type="button" onClick={showExistingCustomers}>
                                    <FiChevronLeft />
                                    <span>Volver a clientes</span>
                                </button>
                            </div>
                        ) : (
                            <>
                                <label className="search-field">Buscar por nombre, telefono o ubicacion
                                    <FiSearch />
                                    <input
                                        placeholder="Ej. Maria, 5555, Mixco..."
                                        value={customerSearch}
                                        onChange={(event) => setCustomerSearch(event.target.value)}
                                    />
                                </label>
                                {selectedCustomer && (
                                    <div className="selected-customer">
                                        <strong>{selectedCustomer.fullName}</strong>
                                        <span>{selectedCustomer.phone}</span>
                                        <small>{selectedCustomer.location?.department || 'Sin departamento'} · {selectedCustomer.location?.municipality || 'Sin municipio'}</small>
                                    </div>
                                )}
                                <div className="customer-results">
                                    {customerLoading ? (
                                        <TableEmptyState loading emptyText="Buscando clientes..." />
                                    ) : customers.length === 0 ? (
                                        <div className="customer-empty">
                                            <strong>No encontre clientes</strong>
                                            <span>Presiona Nuevo para registrar uno en esta venta.</span>
                                        </div>
                                    ) : (
                                        customers.map((customer) => (
                                            <button
                                                className={form.customerId === customer._id ? 'active' : ''}
                                                key={customer._id}
                                                type="button"
                                                onClick={() => selectExistingCustomer(customer)}
                                            >
                                                <strong>{customer.fullName}</strong>
                                                <span>{customer.phone}</span>
                                                <small>{customer.location?.department || 'Sin departamento'} · {customer.location?.municipality || 'Sin municipio'}</small>
                                            </button>
                                        ))
                                    )}
                                </div>
                                <div className="customer-pagination">
                                    <button
                                        className="icon-button"
                                        type="button"
                                        disabled={customerLoading || customerPage.page <= 1}
                                        onClick={() => loadCustomers({ page: customerPage.page - 1 })}
                                        aria-label="Pagina anterior de clientes"
                                    >
                                        <FiChevronLeft />
                                    </button>
                                    <span>Pagina {customerPage.page} de {customerPage.pages}</span>
                                    <button
                                        className="icon-button"
                                        type="button"
                                        disabled={customerLoading || customerPage.page >= customerPage.pages}
                                        onClick={() => loadCustomers({ page: customerPage.page + 1 })}
                                        aria-label="Pagina siguiente de clientes"
                                    >
                                        <FiChevronRight />
                                    </button>
                                </div>
                            </>
                        )}
                    </section>
                    <label>Fecha de envio<input type="date" value={form.shipDate} onChange={(e) => setForm({ ...form, shipDate: e.target.value })} required /></label>
                </div>
                {customerMode === 'new' && (
                    <section className="new-customer-fields">
                        <div className="form-title-row">
                            <h3>Datos del cliente nuevo</h3>
                            <button className="ghost-button muted" type="button" onClick={showExistingCustomers}>
                                <FiX />
                                <span>Cancelar nuevo</span>
                            </button>
                        </div>
                        <div className="form-grid three">
                            <label>Nombre<input value={form.customer.fullName} onChange={(e) => setForm({ ...form, customer: { ...form.customer, fullName: e.target.value } })} required /></label>
                            <label>Telefono<input value={form.customer.phone} onChange={(e) => setForm({ ...form, customer: { ...form.customer, phone: e.target.value } })} required /></label>
                            <label>Departamento
                                <select value={form.customer.location.department} onChange={(e) => setForm({ ...form, customer: { ...form.customer, location: { department: e.target.value, municipality: '', zone: '' } } })} required>
                                    <option value="">Seleccionar departamento</option>
                                    {departments.map((department) => <option key={department.code} value={department.name}>{department.name}</option>)}
                                </select>
                            </label>
                        </div>
                        <div className="form-grid three">
                            <label>Municipio
                                <select value={form.customer.location.municipality} onChange={(e) => setForm({ ...form, customer: { ...form.customer, location: { ...form.customer.location, municipality: e.target.value, zone: '' } } })} disabled={!form.customer.location.department} required>
                                    <option value="">Seleccionar municipio</option>
                                    {customerMunicipalities.map((municipality) => <option key={municipality.code} value={municipality.name}>{municipality.name}</option>)}
                                </select>
                            </label>
                            <label>Zona
                                <select value={form.customer.location.zone} onChange={(e) => setForm({ ...form, customer: { ...form.customer, location: { ...form.customer.location, zone: e.target.value } } })} disabled={!form.customer.location.municipality}>
                                    <option value="">Sin zona</option>
                                    {zoneOptions.map((zone) => <option key={zone} value={zone}>{zone}</option>)}
                                </select>
                            </label>
                            <label>Direccion<input value={form.customer.address} onChange={(e) => setForm({ ...form, customer: { ...form.customer, address: e.target.value } })} required /></label>
                        </div>
                    </section>
                )}
                <div className="line-items">
                    {form.items.map((item, index) => (
                        <div className="line-item" key={`${index}-${item.code}`}>
                            <select value={item.code} onChange={(e) => updateItem(index, { code: e.target.value })} required>
                                <option value="">Codigo</option>
                                {jewelry.map((jewel) => <option key={jewel._id} value={jewel.code}>{jewel.code} - stock {jewel.stock}</option>)}
                            </select>
                            <input type="number" min="0" step="0.01" placeholder="Precio" value={item.salePrice} onChange={(e) => updateItem(index, { salePrice: e.target.value })} required />
                            <button className="icon-button" type="button" onClick={() => setForm({ ...form, items: form.items.filter((_, i) => i !== index) })} aria-label="Eliminar joya">
                                <FiTrash2 />
                            </button>
                        </div>
                    ))}
                    <button className="ghost-button" type="button" disabled={saving} onClick={() => setForm({ ...form, items: [...form.items, { code: '', salePrice: '' }] })}>
                        <FiPlus /><span>Agregar joya</span>
                    </button>
                </div>
                <div className="form-grid three">
                    <label>Deposito
                        <select value={form.deposit.hasDeposit ? 'yes' : 'no'} onChange={(e) => setForm({ ...form, deposit: { ...form.deposit, hasDeposit: e.target.value === 'yes' } })}>
                            <option value="no">No</option><option value="yes">Si</option>
                        </select>
                    </label>
                    <label>Monto deposito<input type="number" min="0" step="0.01" disabled={!form.deposit.hasDeposit} value={form.deposit.amount} onChange={(e) => setForm({ ...form, deposit: { ...form.deposit, amount: e.target.value } })} /></label>
                    <label>Banco
                        <select disabled={!form.deposit.hasDeposit} value={form.deposit.bank} onChange={(e) => setForm({ ...form, deposit: { ...form.deposit, bank: e.target.value } })}>
                            <option>BI</option><option>Banrural</option><option>BAC</option><option>Promerica</option><option>Otro</option>
                        </select>
                    </label>
                </div>
                <div className="form-grid four">
                    <label>Envio
                        <select value={form.shipping.provider} onChange={(e) => setForm({ ...form, shipping: { ...form.shipping, provider: e.target.value, mode: 'standard', customerCharge: e.target.value === 'mensajero' ? 25 : 35 } })}>
                            <option value="mensajero">Mensajero</option><option value="forza">Forza</option><option value="caex">CAEX</option>
                        </select>
                    </label>
                    <label>Modalidad
                        <select value={form.shipping.mode} onChange={(e) => setForm({ ...form, shipping: { ...form.shipping, mode: e.target.value } })}>
                            {shippingModeOptions.map((option) => <option key={option} value={option}>{shippingModeLabels[option]}</option>)}
                        </select>
                    </label>
                    <label>Cobro envio<input type="number" min="0" step="0.01" value={form.shipping.customerCharge} onChange={(e) => setForm({ ...form, shipping: { ...form.shipping, customerCharge: e.target.value } })} /></label>
                    <label>COD
                        <select disabled={form.shipping.mode !== 'cod'} value={form.shipping.codChargeType} onChange={(e) => setForm({ ...form, shipping: { ...form.shipping, codChargeType: e.target.value } })}>
                            {codChargeTypes.map((option) => <option key={option} value={option}>{codChargeTypeLabels[option]}</option>)}
                        </select>
                    </label>
                </div>
                <button className="primary-button" type="submit" disabled={saving || loading}>
                    {saving ? <ButtonSpinner /> : <FiPlus />}
                    <span>{saving ? 'Guardando...' : 'Registrar venta'}</span>
                </button>
            </form>
            <div className="panel">
                <div className="table-toolbar">
                    <div>
                        <h2>Ventas</h2>
                        <span className="table-subtitle">{salesPage.total} venta{salesPage.total === 1 ? '' : 's'} encontrada{salesPage.total === 1 ? '' : 's'}</span>
                    </div>
                    <button className={`icon-button ${loading ? 'is-spinning' : ''}`} type="button" onClick={() => load({ page: salesPage.page })} disabled={loading} aria-label="Actualizar"><FiRefreshCw /></button>
                </div>
                <form className="filter-panel" onSubmit={applyFilters}>
                    <label className="search-field">Buscar venta
                        <FiSearch />
                        <input placeholder="Cliente, telefono, codigo o ubicacion" value={salesSearch} onChange={(event) => setSalesSearch(event.target.value)} />
                    </label>
                    <label>Filtro
                        <select value={filters.mode} onChange={(event) => setFilters({ ...filters, mode: event.target.value })}>
                            <option value="all">Todo</option>
                            <option value="day">Por dia</option>
                            <option value="month">Por mes</option>
                            <option value="range">Rango</option>
                        </select>
                    </label>
                    {filters.mode === 'day' && (
                        <label>Dia
                            <input type="date" value={filters.day} onChange={(event) => setFilters({ ...filters, day: event.target.value })} />
                        </label>
                    )}
                    {filters.mode === 'month' && (
                        <label>Mes
                            <input type="month" value={filters.month} onChange={(event) => setFilters({ ...filters, month: event.target.value })} />
                        </label>
                    )}
                    {filters.mode === 'range' && (
                        <>
                            <label>Desde
                                <input type="date" value={filters.from} onChange={(event) => setFilters({ ...filters, from: event.target.value })} />
                            </label>
                            <label>Hasta
                                <input type="date" value={filters.to} onChange={(event) => setFilters({ ...filters, to: event.target.value })} />
                            </label>
                        </>
                    )}
                    <button className="ghost-button" type="submit" disabled={loading}>
                        <FiFilter />
                        <span>Filtrar</span>
                    </button>
                    <button className="ghost-button muted" type="button" disabled={loading} onClick={clearFilters}>
                        <FiX />
                        <span>Limpiar</span>
                    </button>
                </form>
                {editingSaleId && saleEdit && (
                    <form className={`edit-panel ${saleSaving ? 'is-busy' : ''}`} onSubmit={submitSaleEdit}>
                        {saleSaving && <PanelLoader label="Actualizando venta..." />}
                        <div className="form-title-row">
                            <h3>Editar venta</h3>
                            <button className="ghost-button muted" type="button" onClick={cancelEditSale}><FiX /><span>Cancelar</span></button>
                        </div>
                        <div className="form-grid three">
                            <label>Estado
                                <select value={saleEdit.status} onChange={(event) => setSaleEdit({ ...saleEdit, status: event.target.value })}>
                                    {statusOptions.map((option) => <option key={option} value={option}>{statusLabels[option]}</option>)}
                                </select>
                            </label>
                            <label>Fecha de envio
                                <input type="date" value={saleEdit.shipDate} onChange={(event) => setSaleEdit({ ...saleEdit, shipDate: event.target.value })} />
                            </label>
                            <label>Deposito
                                <select value={saleEdit.deposit.hasDeposit ? 'yes' : 'no'} onChange={(event) => setSaleEdit({ ...saleEdit, deposit: { ...saleEdit.deposit, hasDeposit: event.target.value === 'yes' } })}>
                                    <option value="no">No</option><option value="yes">Si</option>
                                </select>
                            </label>
                        </div>
                        <div className="form-grid three">
                            <label>Departamento
                                <select value={saleEdit.customerSnapshot.location.department} onChange={(event) => setSaleEdit({ ...saleEdit, customerSnapshot: { ...saleEdit.customerSnapshot, location: { department: event.target.value, municipality: '', zone: '' } } })}>
                                    <option value="">Seleccionar departamento</option>
                                    {departments.map((department) => <option key={department.code} value={department.name}>{department.name}</option>)}
                                </select>
                            </label>
                            <label>Municipio
                                <select value={saleEdit.customerSnapshot.location.municipality} onChange={(event) => setSaleEdit({ ...saleEdit, customerSnapshot: { ...saleEdit.customerSnapshot, location: { ...saleEdit.customerSnapshot.location, municipality: event.target.value, zone: '' } } })} disabled={!saleEdit.customerSnapshot.location.department}>
                                    <option value="">Seleccionar municipio</option>
                                    {editMunicipalities.map((municipality) => <option key={municipality.code} value={municipality.name}>{municipality.name}</option>)}
                                </select>
                            </label>
                            <label>Zona
                                <select value={saleEdit.customerSnapshot.location.zone} onChange={(event) => setSaleEdit({ ...saleEdit, customerSnapshot: { ...saleEdit.customerSnapshot, location: { ...saleEdit.customerSnapshot.location, zone: event.target.value } } })} disabled={!saleEdit.customerSnapshot.location.municipality}>
                                    <option value="">Sin zona</option>
                                    {zoneOptions.map((zone) => <option key={zone} value={zone}>{zone}</option>)}
                                </select>
                            </label>
                        </div>
                        <label>Direccion de envio
                            <textarea value={saleEdit.customerSnapshot.address} onChange={(event) => setSaleEdit({ ...saleEdit, customerSnapshot: { ...saleEdit.customerSnapshot, address: event.target.value } })} required />
                        </label>
                        <div className="form-grid three">
                            <label>Monto deposito
                                <input type="number" min="0" step="0.01" disabled={!saleEdit.deposit.hasDeposit} value={saleEdit.deposit.amount} onChange={(event) => setSaleEdit({ ...saleEdit, deposit: { ...saleEdit.deposit, amount: event.target.value } })} />
                            </label>
                            <label>Banco
                                <select disabled={!saleEdit.deposit.hasDeposit} value={saleEdit.deposit.bank} onChange={(event) => setSaleEdit({ ...saleEdit, deposit: { ...saleEdit.deposit, bank: event.target.value } })}>
                                    <option>BI</option><option>Banrural</option><option>BAC</option><option>Promerica</option><option>Otro</option>
                                </select>
                            </label>
                            <label>Envio
                                <select value={saleEdit.shipping.provider} onChange={(event) => setSaleEdit({ ...saleEdit, shipping: { ...saleEdit.shipping, provider: event.target.value, mode: 'standard', customerCharge: event.target.value === 'mensajero' ? 25 : 35 } })}>
                                    {shippingProviders.map((option) => <option key={option} value={option}>{shippingProviderLabels[option]}</option>)}
                                </select>
                            </label>
                        </div>
                        <div className="form-grid three">
                            <label>Modalidad
                                <select value={saleEdit.shipping.mode} onChange={(event) => setSaleEdit({ ...saleEdit, shipping: { ...saleEdit.shipping, mode: event.target.value } })}>
                                    {editShippingModeOptions.map((option) => <option key={option} value={option}>{shippingModeLabels[option]}</option>)}
                                </select>
                            </label>
                            <label>Cobro envio
                                <input type="number" min="0" step="0.01" value={saleEdit.shipping.customerCharge} onChange={(event) => setSaleEdit({ ...saleEdit, shipping: { ...saleEdit.shipping, customerCharge: event.target.value } })} />
                            </label>
                            <label>COD
                                <select disabled={saleEdit.shipping.mode !== 'cod'} value={saleEdit.shipping.codChargeType} onChange={(event) => setSaleEdit({ ...saleEdit, shipping: { ...saleEdit.shipping, codChargeType: event.target.value } })}>
                                    {codChargeTypes.map((option) => <option key={option} value={option}>{codChargeTypeLabels[option]}</option>)}
                                </select>
                            </label>
                        </div>
                        <label>Notas
                            <textarea value={saleEdit.notes} onChange={(event) => setSaleEdit({ ...saleEdit, notes: event.target.value })} />
                        </label>
                        <button className="primary-button" type="submit" disabled={saleSaving}>
                            {saleSaving ? <ButtonSpinner /> : <FiPlus />}
                            <span>{saleSaving ? 'Guardando...' : 'Actualizar venta'}</span>
                        </button>
                    </form>
                )}
                <div className="table-wrap">
                    {loading || filteredSales.length === 0 ? (
                        <TableEmptyState loading={loading} emptyText="Todavia no hay ventas registradas." />
                    ) : (
                        <table>
                            <thead><tr><th>Fecha venta</th><th>Cliente</th><th>Total</th><th>Envio</th>{user.role === 'admin' && <th>Ganancia</th>}<th>Estado</th><th>Acciones</th></tr></thead>
                            <tbody>
                                {filteredSales.map((sale) => (
                                    <React.Fragment key={sale._id}>
                                        <tr className="sale-row" onClick={() => setSelectedSale(sale)}>
                                            <td>{formatDisplayDate(sale.createdAt)}</td>
                                            <td>{sale.customerSnapshot?.fullName}<small>{sale.customerSnapshot?.phone}</small></td>
                                            <td>{currency.format(sale.totals?.saleTotal || 0)}</td>
                                            <td>{labelFor(shippingProviderLabels, sale.shipping?.provider)} {labelFor(shippingModeLabels, sale.shipping?.mode)}</td>
                                            {user.role === 'admin' && <td>{currency.format(sale.totals?.netProfit || 0)}</td>}
                                            <td>{labelFor(statusLabels, sale.status)}</td>
                                            <td>
                                                <div className="row-actions">
                                                    <button className="icon-button" type="button" onClick={(event) => { event.stopPropagation(); beginEditSale(sale); }} aria-label="Editar venta"><FiEdit2 /></button>
                                                    {user.role === 'admin' && <button className="icon-button danger" type="button" onClick={(event) => { event.stopPropagation(); deleteSale(sale); }} aria-label="Eliminar venta"><FiTrash2 /></button>}
                                                </div>
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                <div className="pagination-bar">
                    <button className="ghost-button" type="button" disabled={loading || salesPage.page <= 1} onClick={() => load({ page: salesPage.page - 1 })}>
                        <FiChevronLeft />
                        <span>Anterior</span>
                    </button>
                    <span>Pagina {salesPage.page} de {salesPage.pages}</span>
                    <button className="ghost-button" type="button" disabled={loading || salesPage.page >= salesPage.pages} onClick={() => load({ page: salesPage.page + 1 })}>
                        <span>Siguiente</span>
                        <FiChevronRight />
                    </button>
                </div>
            </div>
            {selectedSale && renderSaleBreakdown(selectedSale)}
        </section>
    );
};

export default Sales;
